/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DependencyList,
  type ReactNode,
} from 'react';
import { WorkspaceHeader } from './WorkspaceHeader';
import { useSettingsWorkspaceLayout } from '@/contexts/settings-workspace-layout';
import { cn } from '@/lib/utils';

type WorkspaceHeaderSlotKey =
  | 'contextHeader'
  | 'toolbarActions'
  | 'viewToggle'
  | 'quickActions'
  | 'newButton';

type WorkspaceHeaderSlot = {
  owner: symbol;
  node: ReactNode;
};

type WorkspaceHeaderSlotState = Partial<Record<WorkspaceHeaderSlotKey, WorkspaceHeaderSlot>>;

type WorkspaceHeaderSlotInput = Partial<Record<WorkspaceHeaderSlotKey, ReactNode>>;

interface WorkspaceHeaderContextValue {
  setSlot: (key: WorkspaceHeaderSlotKey, node: ReactNode, owner: symbol) => void;
  clearSlot: (key: WorkspaceHeaderSlotKey, owner: symbol) => void;
}

interface WorkspaceShellProps {
  hideHeader?: boolean;
  children: ReactNode;
}

const WorkspaceHeaderContext = createContext<WorkspaceHeaderContextValue | null>(null);

export function WorkspaceShell({ hideHeader = false, children }: WorkspaceShellProps) {
  const [slots, setSlots] = useState<WorkspaceHeaderSlotState>({});
  const inSettingsWorkspace = useSettingsWorkspaceLayout();

  const setSlot = useCallback((key: WorkspaceHeaderSlotKey, node: ReactNode, owner: symbol) => {
    setSlots((current) => {
      const existing = current[key];
      if (existing?.owner === owner && existing.node === node) return current;
      return { ...current, [key]: { owner, node } };
    });
  }, []);

  const clearSlot = useCallback((key: WorkspaceHeaderSlotKey, owner: symbol) => {
    setSlots((current) => {
      if (current[key]?.owner !== owner) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      setSlot,
      clearSlot,
    }),
    [clearSlot, setSlot],
  );

  return (
    <WorkspaceHeaderContext.Provider value={contextValue}>
      <div className={cn('h-full min-h-0 p-0', !inSettingsWorkspace && 'md:p-3')}>
        <div
          className={cn(
            'flex h-full min-h-0 flex-col overflow-hidden rounded-none border-0 bg-card shadow-none',
            !inSettingsWorkspace && 'md:rounded-xl md:border md:shadow-sm',
          )}
        >
          {hideHeader ? null : (
            <WorkspaceHeader
              contextHeader={slots.contextHeader?.node}
              toolbarActions={slots.toolbarActions?.node}
              viewToggle={slots.viewToggle?.node}
              quickActions={slots.quickActions?.node}
              newButton={slots.newButton?.node}
            />
          )}
          {children}
        </div>
      </div>
    </WorkspaceHeaderContext.Provider>
  );
}

export function useWorkspaceHeader(slots: WorkspaceHeaderSlotInput, deps: DependencyList = []) {
  const context = useContext(WorkspaceHeaderContext);
  const ownerRef = useRef<symbol | null>(null);
  const slotsRef = useRef(slots);
  slotsRef.current = slots;

  if (!ownerRef.current) {
    ownerRef.current = Symbol('workspace-header-slot-owner');
  }

  useEffect(() => {
    if (!context || !ownerRef.current) return;

    const owner = ownerRef.current;
    const entries = Object.entries(slotsRef.current) as Array<[WorkspaceHeaderSlotKey, ReactNode]>;

    entries.forEach(([key, node]) => {
      if (node === null || node === undefined || node === false) {
        context.clearSlot(key, owner);
        return;
      }
      context.setSlot(key, node, owner);
    });

    return () => {
      entries.forEach(([key]) => context.clearSlot(key, owner));
    };
    // Callers provide the slot update dependencies explicitly so JSX slot nodes do not
    // force a header state update on every provider render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, ...deps]);

  return Boolean(context);
}
